import { MonteCarloDashboard } from '@/components/MonteCarloDashboard'
import MonteCarloInsights from './MonteCarloInsights'

function calculateMetricsFromTrades(trades) {
  if (!trades || trades.length === 0) {
    return {
      total_trades: 0,
      net_profit_pct: 0,
      win_rate: 0,
      profit_factor: 0,
      max_drawdown_pct: 0,
      expectancy: 0,
      sharpe: 0,
      risk_of_ruin: 0,
    }
  }

  const returns = trades.map((t) => t.pnl_pct || 0)
  const winners = returns.filter((r) => r > 0)
  const losers = returns.filter((r) => r < 0)

  const netProfitPct = returns.reduce((a, b) => a + b, 0)
  const winRate = winners.length / trades.length

  const grossProfit = winners.reduce((a, b) => a + b, 0)
  const grossLoss = Math.abs(losers.reduce((a, b) => a + b, 0))
  const profitFactor = grossLoss > 0 ? grossProfit / grossLoss : grossProfit > 0 ? Infinity : 0

  const expectancy = returns.reduce((a, b) => a + b, 0) / returns.length
  const std = Math.sqrt(returns.reduce((sq, n) => sq + (n - expectancy) ** 2, 0) / returns.length)
  const sharpe = std > 0 ? expectancy / std : 0

  // Max drawdown from equity curve
  let equity = 1
  let peak = 1
  let maxDd = 0
  for (const r of returns) {
    equity *= 1 + r
    if (equity > peak) peak = equity
    const dd = (peak - equity) / peak
    if (dd > maxDd) maxDd = dd
  }

  // Risk of ruin approximation
  const avgWin = winners.length ? winners.reduce((a, b) => a + b, 0) / winners.length : 0
  const avgLoss = losers.length ? Math.abs(losers.reduce((a, b) => a + b, 0)) / losers.length : 0
  let riskOfRuin = 0
  if (avgLoss > 0 && winRate > 0 && winRate < 1) {
    riskOfRuin = Math.min(1, Math.max(0, ((1 - winRate) / winRate) ** (1 / (avgLoss * 100))))
  }

  return {
    total_trades: trades.length,
    net_profit_pct: netProfitPct,
    win_rate: winRate,
    profit_factor: profitFactor === Infinity ? 999 : profitFactor,
    max_drawdown_pct: maxDd,
    expectancy,
    sharpe,
    risk_of_ruin: riskOfRuin,
  }
}

function extractMonteCarloPercentiles(mc, summary, tradesMetrics) {
  // Prefer real Monte Carlo percentiles from single result
  const singleResult = mc.result || {}
  const pe = singleResult.percentiles_extended || {}

  if (pe.net_profit_pct) {
    return {
      score: mc.validation?.score ?? 0,
      scoreLabel: mc.validation?.score_label || 'drawdown_robustness',
      status: (mc.validation?.passed || (mc.validation?.score ?? 0) >= 80) ? 'approved' : (mc.validation?.score ?? 0) >= 70 ? 'marginal' : 'rejected',
      netProfit: { median: pe.net_profit_pct.p50 ?? tradesMetrics.net_profit_pct, p5: pe.net_profit_pct.p5 ?? 0, p95: pe.net_profit_pct.p95 ?? 0 },
      winRate: { median: pe.win_rate?.p50 ?? tradesMetrics.win_rate, p5: pe.win_rate?.p5 ?? 0, p95: pe.win_rate?.p95 ?? 0 },
      profitFactor: { median: pe.profit_factor?.p50 ?? tradesMetrics.profit_factor, p5: pe.profit_factor?.p5 ?? 0, p95: pe.profit_factor?.p95 ?? 0 },
      maxDrawdown: { median: -(pe.max_drawdown?.p50 ?? tradesMetrics.max_drawdown_pct), p5: -(pe.max_drawdown?.p95 ?? 0), p95: -(pe.max_drawdown?.p5 ?? 0) },
      sharpe: { median: pe.sharpe?.p50 ?? tradesMetrics.sharpe, p5: pe.sharpe?.p5 ?? 0, p95: pe.sharpe?.p95 ?? 0 },
      riskOfRuin: tradesMetrics.risk_of_ruin,
      probabilityLoss: mc.probability_loss ?? 0,
    }
  }

  // Fallback for batch summary (no real percentiles): use avg as median and derive rough spread
  const netMedian = summary.avg_net_profit_pct ?? tradesMetrics.net_profit_pct
  const winMedian = summary.avg_win_rate ?? tradesMetrics.win_rate
  const pfMedian = summary.avg_profit_factor ?? tradesMetrics.profit_factor
  const ddMedian = -(summary.worst_max_drawdown_pct ?? tradesMetrics.max_drawdown_pct)

  return {
    score: summary.avg_score ?? 0,
    scoreLabel: 'drawdown_robustness',
    status: (summary.avg_score ?? 0) >= 80 ? 'approved' : (summary.avg_score ?? 0) >= 70 ? 'marginal' : 'rejected',
    netProfit: { median: netMedian, p5: netMedian * 0.5, p95: netMedian * 1.5 },
    winRate: { median: winMedian, p5: Math.max(0, winMedian - 0.15), p95: Math.min(1, winMedian + 0.15) },
    profitFactor: { median: pfMedian, p5: Math.max(0, pfMedian * 0.5), p95: pfMedian * 1.5 },
    maxDrawdown: { median: ddMedian, p5: ddMedian * 1.5, p95: ddMedian * 0.5 },
    sharpe: { median: summary.avg_sharpe ?? tradesMetrics.sharpe, p5: 0, p95: (summary.avg_sharpe ?? tradesMetrics.sharpe) * 1.5 },
    riskOfRuin: tradesMetrics.risk_of_ruin,
    probabilityLoss: 0,
  }
}

function transformSingleResult(result) {
  const metrics = result.metrics || {}
  const mc = result.monte_carlo || {}
  const mcResult = mc.result || {}
  const validation = mc.validation || {}

  return {
    asset: result.asset || '—',
    timeframe: result.timeframe || '—',
    historicalDays: result.lookback_days || 180,
    simulations: mcResult.n_simulations || 5000,
    totalTrades: metrics.total_trades || result.trades_count || 0,
    backtest: {
      netProfitPct: metrics.net_profit_pct ?? 0,
      winRate: metrics.win_rate ?? 0,
      profitFactor: metrics.profit_factor ?? 0,
      maxDrawdownPct: metrics.max_drawdown_pct ?? 0,
      expectancy: metrics.expectancy ?? 0,
      sharpe: metrics.sharpe ?? 0,
      riskOfRuin: metrics.risk_of_ruin ?? 0,
    },
    monteCarlo: extractMonteCarloPercentiles(mc, {}, metrics),
  }
}

function transformBatchResult(result) {
  const summary = result.summary || {}
  const first = (result.by_asset_timeframe || [])[0]

  if (!first) {
    return null
  }

  const trades = first.trades || []
  const tradesMetrics = calculateMetricsFromTrades(trades)
  const mc = result.monte_carlo || {}

  return {
    asset: first.asset || 'Scanner',
    timeframe: first.timeframe || 'Batch',
    historicalDays: summary.lookback_days || 180,
    simulations: summary.n_simulations || 5000,
    totalTrades: summary.total_trades || tradesMetrics.total_trades || 0,
    backtest: {
      netProfitPct: tradesMetrics.net_profit_pct,
      winRate: tradesMetrics.win_rate,
      profitFactor: tradesMetrics.profit_factor,
      maxDrawdownPct: tradesMetrics.max_drawdown_pct,
      expectancy: tradesMetrics.expectancy,
      sharpe: tradesMetrics.sharpe,
      riskOfRuin: tradesMetrics.risk_of_ruin,
    },
    monteCarlo: extractMonteCarloPercentiles(mc, summary, tradesMetrics),
  }
}

function aggregateSignalsByIndicator(result) {
  const combos = result.by_asset_timeframe || result.results || []
  const totals = {}
  for (const combo of combos) {
    const counts = combo.signals_by_indicator || {}
    for (const [ind, count] of Object.entries(counts)) {
      totals[ind] = (totals[ind] || 0) + count
    }
  }
  return totals
}

function formatPct(v) {
  if (v === undefined || v === null || Number.isNaN(v)) return '—'
  return `${(Number(v) * 100).toFixed(2)}%`
}

function formatNum(v, digits = 2) {
  if (v === undefined || v === null || Number.isNaN(v)) return '—'
  return Number(v).toFixed(digits)
}

function BatchMonteCarloSummary({ result }) {
  const summary = result.summary || {}
  const byAsset = result.by_asset || []
  const byTimeframe = result.by_timeframe || []

  return (
    <div className="p-4 space-y-6 bg-slate-900/50 border border-slate-700 rounded-xl text-slate-200">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">Resultado Batch Monte Carlo</h2>
        <span className="text-xs text-slate-400">{summary.n_simulations?.toLocaleString()} simulaciones · {summary.lookback_days} días</span>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <div className="bg-slate-800 rounded-lg p-3">
          <p className="text-[10px] text-slate-400 uppercase">Trades totales</p>
          <p className="text-xl font-bold">{summary.total_trades ?? 0}</p>
        </div>
        <div className="bg-slate-800 rounded-lg p-3">
          <p className="text-[10px] text-slate-400 uppercase">Score medio</p>
          <p className="text-xl font-bold">{formatNum(summary.avg_score, 1)}</p>
        </div>
        <div className="bg-slate-800 rounded-lg p-3">
          <p className="text-[10px] text-slate-400 uppercase">Net profit medio</p>
          <p className="text-xl font-bold">{formatPct(summary.avg_net_profit_pct)}</p>
        </div>
        <div className="bg-slate-800 rounded-lg p-3">
          <p className="text-[10px] text-slate-400 uppercase">Peor drawdown</p>
          <p className="text-xl font-bold">{formatPct(summary.worst_max_drawdown_pct)}</p>
        </div>
      </div>

      <MonteCarloInsights insights={result.insights} />

      {byAsset.length > 0 && (
        <div>
          <h3 className="text-sm font-medium mb-2 text-slate-300">Por activo</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-slate-800 text-slate-400">
                <tr>
                  <th className="text-left p-2 rounded-l-lg">Activo</th>
                  <th className="text-right p-2">Combos</th>
                  <th className="text-right p-2">Trades</th>
                  <th className="text-right p-2">Win rate</th>
                  <th className="text-right p-2">Profit factor</th>
                  <th className="text-right p-2">Sharpe</th>
                  <th className="text-right p-2">Score</th>
                  <th className="text-right p-2 rounded-r-lg">Max DD</th>
                </tr>
              </thead>
              <tbody>
                {byAsset.map((a) => (
                  <tr key={a.asset} className="border-b border-slate-800">
                    <td className="p-2 font-medium">{a.asset}</td>
                    <td className="text-right p-2">{a.combinations}</td>
                    <td className="text-right p-2">{a.total_trades}</td>
                    <td className="text-right p-2">{formatPct(a.avg_win_rate)}</td>
                    <td className="text-right p-2">{formatNum(a.avg_profit_factor, 2)}</td>
                    <td className="text-right p-2">{formatNum(a.avg_sharpe, 2)}</td>
                    <td className="text-right p-2">{formatNum(a.avg_score, 1)}</td>
                    <td className="text-right p-2">{formatPct(a.max_drawdown_pct)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {byTimeframe.length > 0 && (
        <div>
          <h3 className="text-sm font-medium mb-2 text-slate-300">Por timeframe</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-slate-800 text-slate-400">
                <tr>
                  <th className="text-left p-2 rounded-l-lg">Timeframe</th>
                  <th className="text-right p-2">Combos</th>
                  <th className="text-right p-2">Trades</th>
                  <th className="text-right p-2">Win rate</th>
                  <th className="text-right p-2">Profit factor</th>
                  <th className="text-right p-2">Sharpe</th>
                  <th className="text-right p-2">Score</th>
                  <th className="text-right p-2 rounded-r-lg">Max DD</th>
                </tr>
              </thead>
              <tbody>
                {byTimeframe.map((tf) => (
                  <tr key={tf.timeframe} className="border-b border-slate-800">
                    <td className="p-2 font-medium">{tf.timeframe}</td>
                    <td className="text-right p-2">{tf.combinations}</td>
                    <td className="text-right p-2">{tf.total_trades}</td>
                    <td className="text-right p-2">{formatPct(tf.avg_win_rate)}</td>
                    <td className="text-right p-2">{formatNum(tf.avg_profit_factor, 2)}</td>
                    <td className="text-right p-2">{formatNum(tf.avg_sharpe, 2)}</td>
                    <td className="text-right p-2">{formatNum(tf.avg_score, 1)}</td>
                    <td className="text-right p-2">{formatPct(tf.max_drawdown_pct)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  )
}

export default function ScannerMonteCarloDashboard({ result }) {
  if (!result) return null

  const isBatch = result.mode === 'batch' || !!result.summary

  if (isBatch) {
    const totalTrades = result.summary?.total_trades ?? 0
    const totalCombinations = result.summary?.total_combinations ?? 0

    if (totalCombinations === 0) {
      return (
        <div className="p-6 text-center border border-dashed border-amber-600 rounded-xl bg-amber-900/20 text-amber-100 space-y-3">
          <p className="font-semibold text-lg">Análisis incompleto</p>
          <p className="text-sm text-amber-200/80">
            Ninguna combinación activo/timeframe pudo analizarse. Esto suele pasar cuando el job anterior falló internamente.
          </p>
          <p className="text-xs text-amber-200/60">
            Descarta este job (✕) y vuelve a ejecutar Monte Carlo. Si el problema persiste, revisa los logs.
          </p>
        </div>
      )
    }

    if (totalTrades === 0) {
      const signals = aggregateSignalsByIndicator(result)
      const signalEntries = Object.entries(signals)
      return (
        <div className="p-6 text-center border border-dashed border-slate-600 rounded-xl bg-slate-900/50 text-slate-300 space-y-3">
          <p className="font-semibold text-lg">0 trades generados</p>
          <p className="text-sm text-slate-400">
            El análisis batch se completó, pero ningún indicador produjo señales válidas en el periodo/timeframes seleccionados.
          </p>
          {signalEntries.length > 0 ? (
            <div className="inline-block text-left text-xs bg-slate-800 rounded-lg p-3">
              <p className="font-medium mb-1">Señales detectadas por indicador:</p>
              <ul className="space-y-1">
                {signalEntries.map(([ind, count]) => (
                  <li key={ind} className="flex justify-between gap-6">
                    <span className="text-slate-400">{ind}</span>
                    <span className={count > 0 ? 'text-emerald-400' : 'text-red-400'}>{count}</span>
                  </li>
                ))}
              </ul>
            </div>
          ) : (
            <p className="text-xs text-slate-500">
              Ningún indicador del scanner emitió señales long/short. Revisa la configuración del scanner o prueba más datos/otro timeframe.
            </p>
          )}
        </div>
      )
    }

    return <BatchMonteCarloSummary result={result} />
  }

  const props = transformSingleResult(result)

  if (!props) {
    return (
      <div className="p-8 text-center text-slate-400">
        No hay datos de Monte Carlo disponibles para mostrar.
      </div>
    )
  }

  if (props.totalTrades === 0) {
    const signals = aggregateSignalsByIndicator(result)
    const signalEntries = Object.entries(signals)
    return (
      <div className="p-6 text-center border border-dashed border-slate-600 rounded-xl bg-slate-900/50 text-slate-300 space-y-3">
        <p className="font-semibold text-lg">0 trades generados</p>
        <p className="text-sm text-slate-400">
          El análisis se completó, pero ningún indicador produjo señales válidas en el periodo/timeframes seleccionados.
        </p>
        {signalEntries.length > 0 ? (
          <div className="inline-block text-left text-xs bg-slate-800 rounded-lg p-3">
            <p className="font-medium mb-1">Señales detectadas por indicador:</p>
            <ul className="space-y-1">
              {signalEntries.map(([ind, count]) => (
                <li key={ind} className="flex justify-between gap-6">
                  <span className="text-slate-400">{ind}</span>
                  <span className={count > 0 ? 'text-emerald-400' : 'text-red-400'}>{count}</span>
                </li>
              ))}
            </ul>
          </div>
        ) : (
          <p className="text-xs text-slate-500">
            Ningún indicador del scanner emitió señales long/short. Revisa la configuración del scanner o prueba más datos/otro timeframe.
          </p>
        )}
      </div>
    )
  }

  return <MonteCarloDashboard {...props} />
}
